// Executive Challenge result screen — the locked report card, challenge-framed
// (dark chrome, design tokens), plus the exported share image. Reuses the
// report-card engine's card data; the framing (player ID, group rank, lock
// note, invite code on the share card) is what makes it a challenge result.

import { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { helpers } from "../util/helpers.ts";
import { logEvent } from "../util/logEvent.ts";
import { ordinal } from "../util/reportCardHelpers.ts";
import {
	challengeGradeColor,
	challengeInviteUrl,
} from "../util/challengeUi.ts";
import SummerLeagueMark from "../components/SummerLeagueMark.tsx";
import {
	FrontOfficeLedgerCard,
	ReportBars,
	ReportBitsStyles,
} from "../components/ReportBits.tsx";
import type { View } from "../../common/types.ts";

type Props = View<"challengeResult">;

// White, theme-independent card rendered off-screen to a PNG. Inline hex only.
// The footer carries the invite code, so a shared screenshot recruits players.
const ChallengeShareCard = ({
	card,
	code,
	ledger,
	playerId,
	rank,
	groupSize,
	result,
	projectedWins,
	teamName,
}: Pick<
	Props,
	| "card"
	| "code"
	| "ledger"
	| "playerId"
	| "rank"
	| "groupSize"
	| "result"
	| "projectedWins"
	| "teamName"
>) => {
	if (!result) {
		return null;
	}
	const color = challengeGradeColor(result.grade);
	const over =
		projectedWins !== undefined
			? Math.round((result.wins - projectedWins) * 10) / 10
			: undefined;

	const chip = (k: string, v: string) => (
		<div
			style={{
				flex: 1,
				background: "#f4f6f7",
				borderRadius: 10,
				padding: "9px 6px",
				textAlign: "center",
			}}
		>
			<div
				style={{
					fontSize: 10,
					fontWeight: 800,
					letterSpacing: 0.3,
					color: "#8a949c",
				}}
			>
				{k}
			</div>
			<div
				style={{
					fontSize: 20,
					fontWeight: 900,
					color: "#121212",
					marginTop: 2,
				}}
			>
				{v}
			</div>
		</div>
	);

	return (
		<div
			style={{
				width: 540,
				fontFamily: "Montserrat, Helvetica, Arial, sans-serif",
				background: "#ffffff",
				borderRadius: 20,
				overflow: "hidden",
				boxSizing: "border-box",
			}}
		>
			<div style={{ padding: "24px 26px 0" }}>
				<div
					style={{
						display: "inline-block",
						background: "#18E0A8",
						color: "#043e2c",
						fontSize: 12,
						fontWeight: 800,
						letterSpacing: 1,
						padding: "6px 12px",
						borderRadius: 7,
					}}
				>
					SUMMER 2026 EXECUTIVE CHALLENGE
				</div>
				<div
					style={{
						display: "flex",
						alignItems: "flex-end",
						justifyContent: "space-between",
						marginTop: 22,
					}}
				>
					<div>
						<div
							style={{
								fontSize: 76,
								fontWeight: 900,
								letterSpacing: -2,
								lineHeight: 0.85,
								color,
							}}
						>
							{result.grade}
						</div>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: 8,
								marginTop: 14,
							}}
						>
							<div style={{ fontSize: 19, fontWeight: 900, color: "#121212" }}>
								{playerId}
							</div>
							<div style={{ fontSize: 15, fontWeight: 700, color: "#6b7680" }}>
								· {teamName}
							</div>
						</div>
						<div
							style={{
								fontSize: 15,
								fontWeight: 700,
								color: "#6b7680",
								marginTop: 3,
							}}
						>
							{result.wins}–{result.losses}
							{rank !== undefined && groupSize > 1
								? ` · ${ordinal(rank)} of ${groupSize}`
								: ""}
						</div>
					</div>
					<div style={{ textAlign: "right" }}>
						<div
							style={{
								fontSize: 12,
								fontWeight: 800,
								letterSpacing: 0.8,
								color: "#8a949c",
							}}
						>
							GM SCORE
						</div>
						<div
							style={{
								fontSize: 56,
								fontWeight: 900,
								letterSpacing: -1.5,
								lineHeight: 1,
								color: "#121212",
							}}
						>
							{result.gmScore}
						</div>
					</div>
				</div>
				<div style={{ display: "flex", gap: 8, marginTop: 20 }}>
					{chip("RANK", rank !== undefined ? `${rank} / ${groupSize}` : "—")}
					{chip("WINS", `${result.wins}`)}
					{chip(
						"OVER",
						over !== undefined ? `${over > 0 ? "+" : ""}${over}` : "—",
					)}
				</div>
				{ledger.bestMove ? (
					<div
						style={{
							display: "flex",
							alignItems: "baseline",
							gap: 8,
							marginTop: 14,
							paddingTop: 12,
							borderTop: "1px solid #e9ecef",
						}}
					>
						<div
							style={{
								fontSize: 10,
								fontWeight: 800,
								letterSpacing: 0.6,
								color: "#8a949c",
								flex: "none",
							}}
						>
							BEST MOVE
						</div>
						<div
							style={{
								fontSize: 14,
								fontWeight: 800,
								color: "#121212",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}
						>
							{ledger.bestMove.name}
						</div>
						<div
							style={{
								fontSize: 14,
								fontWeight: 900,
								color: "#2bc98d",
								flex: "none",
							}}
						>
							+{ledger.bestMove.ewa} wins
						</div>
					</div>
				) : null}
				{card?.champion ? (
					<div
						style={{
							marginTop: 12,
							fontSize: 14,
							fontWeight: 800,
							color: "#121212",
						}}
					>
						🏆 Won the championship
					</div>
				) : null}
			</div>
			<div
				style={{
					marginTop: 22,
					height: 4,
					background: "linear-gradient(90deg,#7C36E6,#FF3D86,#FF7A3C,#18E0A8)",
				}}
			/>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: 11,
					padding: "16px 24px",
					background: "#121212",
				}}
			>
				<SummerLeagueMark size={30} idSuffix="share" />
				<div style={{ lineHeight: 1, flex: 1 }}>
					<div
						style={{
							fontSize: 15,
							fontWeight: 900,
							color: "#fff",
							letterSpacing: 0.4,
						}}
					>
						SUMMER LEAGUE
					</div>
					<div
						style={{
							fontSize: 12,
							fontWeight: 700,
							color: "#8a97a0",
							letterSpacing: 0.3,
							marginTop: 3,
						}}
					>
						Executive Challenge · Code {code}
					</div>
				</div>
			</div>
		</div>
	);
};

const ChallengeResult = ({
	card,
	code,
	groupSize,
	leaderGap,
	leaderPlayerId,
	ledger,
	lost,
	playerId,
	projectedWins,
	rank,
	result,
	status,
	teamName,
	won,
}: Props) => {
	useTitleBar({ title: "Challenge Result" });

	const shareRef = useRef<HTMLDivElement>(null);
	const [sharing, setSharing] = useState(false);

	// White confirmation toast on copy (same look as GmStart's .gm-toast).
	const [toast, setToast] = useState("");
	const toastTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
		undefined,
	);
	const flashToast = (text: string) => {
		clearTimeout(toastTimeout.current);
		setToast(text);
		toastTimeout.current = setTimeout(() => setToast(""), 2200);
	};
	useEffect(() => () => clearTimeout(toastTimeout.current), []);

	// Phase 1 has no hosted share page yet, so "copy link" copies a text invite
	// that carries the join code; Phase 2 swaps the text for the real
	// summerleague.gg share URL. The button is gated on shareId (see `syncing`
	// below) — proof the backend accepted the entry.
	const onCopyLink = async () => {
		if (!result) {
			return;
		}
		try {
			await navigator.clipboard.writeText(
				`My Executive Challenge result: ${result.grade} · GM Score ${result.gmScore} — join me: ${challengeInviteUrl(code)}`,
			);
			flashToast("Share link copied — good for 24 hours");
		} catch {
			// Clipboard unavailable (permissions/insecure context) — the code is
			// visible on the share card anyway.
			flashToast(`Your code: ${code}`);
		}
	};

	const onShare = async () => {
		const el = shareRef.current;
		if (!el || sharing) {
			return;
		}
		setSharing(true);
		try {
			const canvas = await html2canvas(el, {
				backgroundColor: null,
				scale: 2,
				logging: false,
			});
			const blob = await new Promise<Blob | null>((resolve) =>
				canvas.toBlob(resolve, "image/png"),
			);
			if (!blob) {
				throw new Error("Failed to render image");
			}
			const fileName = `executive_challenge_${code}_${playerId}`.toLowerCase();
			const file = new File([blob], `${fileName}.png`, { type: "image/png" });

			const nav = navigator as Navigator & {
				canShare?: (data?: any) => boolean;
				share?: (data?: any) => Promise<void>;
			};
			if (nav.canShare?.({ files: [file] }) && nav.share) {
				try {
					await nav.share({
						files: [file],
						title: "Executive Challenge result",
					});
					return;
				} catch (error) {
					if ((error as { name?: string }).name === "AbortError") {
						return;
					}
				}
			}

			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `${fileName}.png`;
			document.body.append(a);
			a.click();
			a.remove();
			setTimeout(() => URL.revokeObjectURL(url), 1500);
		} catch (error) {
			console.error(error);
			logEvent({
				type: "error",
				text: "Couldn't generate the shareable card.",
				saveToDb: false,
			});
		} finally {
			setSharing(false);
		}
	};

	if (status !== "complete" || !result) {
		return (
			<div className="chr">
				<style>{styles}</style>
				<a className="chr-back" href={helpers.leagueUrl([])}>
					‹ Back
				</a>
				<div className="chr-eyebrow">EXECUTIVE CHALLENGE — IN PROGRESS</div>
				<div className="chr-pending">
					<div className="chr-pending-badge">🏆</div>
					Your result locks in after the 2027 draft. Keep managing — the whole
					group is playing the same frozen season.
				</div>
			</div>
		);
	}

	const color = challengeGradeColor(result.grade);

	// No shareId means the backend never accepted the entry (the worker view
	// retries on every load) — the design's "syncing" state.
	const syncing = !result.shareId;

	let rankLine: string;
	if (syncing) {
		rankLine = "Syncing your result…";
	} else if (groupSize <= 1) {
		rankLine = "Waiting on your group — share the code to compare";
	} else if (rank === 1) {
		rankLine = `1st of ${groupSize} · you lead the board`;
	} else if (rank !== undefined && leaderPlayerId && leaderGap !== undefined) {
		rankLine = `${ordinal(rank)} of ${groupSize} · ${leaderPlayerId} leads by ${leaderGap}`;
	} else {
		rankLine = `${groupSize} on the board`;
	}

	return (
		<div className="chr">
			<style>{styles}</style>
			<ReportBitsStyles />
			<a className="chr-back" href={helpers.leagueUrl([])}>
				‹ Back
			</a>
			<div className="chr-eyebrow">EXECUTIVE CHALLENGE — COMPLETE</div>

			<div
				className="chr-hero"
				style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}
			>
				<div className="chr-hero-circle" />
				<div className="chr-hero-row">
					<div>
						<div className="chr-grade">{result.grade}</div>
						<div className="chr-hero-team">
							<span>{teamName}</span>
							<span className="chr-pid">{playerId}</span>
						</div>
					</div>
					<div className="chr-hero-score">
						<div className="chr-hero-score-label">GM SCORE</div>
						<div className="chr-hero-score-num">{result.gmScore}</div>
					</div>
				</div>
				<div className="chr-rank-line">{rankLine}</div>
			</div>

			{card ? (
				<div className="chr-card">
					<div className="chr-card-label">HOW YOU SCORED</div>
					<ReportBars card={card} won={won} projectedWins={projectedWins} />
				</div>
			) : null}

			<FrontOfficeLedgerCard ledger={ledger} />

			<div className="chr-lock">
				<span>🔒</span> This result is final and can’t be replayed.
			</div>

			{/* Share affordances, design §7 order: link primary, image secondary. */}
			<button
				type="button"
				className="chr-share"
				onClick={onCopyLink}
				disabled={syncing}
			>
				{syncing ? (
					<>
						<span className="chr-spin" />
						Preparing link…
					</>
				) : (
					"🔗 Copy 24-hour share link"
				)}
			</button>
			<button
				type="button"
				className="chr-share chr-share-secondary"
				onClick={onShare}
				disabled={sharing}
			>
				{sharing ? "Rendering…" : "📸 Share card image"}
			</button>
			<a
				className="chr-leaderboard-link"
				href={helpers.leagueUrl(["challenge_leaderboard"])}
			>
				View leaderboard ›
			</a>

			{toast ? <div className="chr-toast">{toast}</div> : null}

			{/* Off-screen source for the shareable PNG. */}
			<div
				aria-hidden
				style={{ position: "fixed", left: -10000, top: 0, zIndex: -1 }}
			>
				<div ref={shareRef}>
					<ChallengeShareCard
						card={card}
						code={code}
						ledger={ledger}
						playerId={playerId}
						rank={rank}
						groupSize={groupSize}
						result={result}
						projectedWins={projectedWins}
						teamName={teamName}
					/>
				</div>
			</div>
		</div>
	);
};

const styles = `
.chr { max-width:560px; margin:0 auto; padding:12px 18px 40px; color:#fff;
  font-family:Montserrat,system-ui,sans-serif; }
.chr-back { display:inline-block; font-size:13px; font-weight:800; color:#9fb0bc;
  text-decoration:none; padding:4px 0 12px; }
.chr-back:hover { color:#cfe0ec; }
.chr-eyebrow { font-size:11px; font-weight:800; letter-spacing:1.4px; color:#18E0A8; }
.chr-pending { margin-top:16px; background:#181818; border:1px solid #232323; border-radius:16px;
  padding:22px 20px; font-size:13px; font-weight:600; color:#9aa4ac; line-height:1.5;
  text-align:center; }
.chr-pending-badge { width:52px; height:52px; border-radius:50%; background:rgba(24,224,168,.16);
  border:1px solid rgba(24,224,168,.4); display:flex; align-items:center; justify-content:center;
  font-size:23px; margin:0 auto 12px; }

.chr-hero { margin-top:12px; border-radius:20px; padding:22px 20px; position:relative;
  overflow:hidden; }
.chr-hero-circle { position:absolute; right:-24px; top:-24px; width:130px; height:130px;
  border-radius:50%; background:rgba(255,255,255,.12); }
.chr-hero-row { position:relative; display:flex; align-items:center; justify-content:space-between; }
.chr-grade { font-size:60px; font-weight:900; letter-spacing:-2px; line-height:.9; color:#062b1f; }
.chr-hero-team { display:flex; align-items:center; gap:7px; margin-top:8px; }
.chr-hero-team span:first-child { font-size:12px; font-weight:900; color:#062b1f; }
.chr-pid { font-size:9px; font-weight:800; letter-spacing:.5px; color:#2bc98d;
  background:#062b1f; padding:2px 6px; border-radius:5px; }
.chr-hero-score { text-align:right; }
.chr-hero-score-label { font-size:10px; font-weight:800; letter-spacing:1px; color:#083a2a; }
.chr-hero-score-num { font-size:38px; font-weight:900; letter-spacing:-1px; line-height:1;
  color:#062b1f; }
.chr-rank-line { position:relative; margin-top:16px; padding-top:13px;
  border-top:1px solid rgba(6,43,31,.22); font-size:13px; font-weight:800; color:#062b1f; }

.chr-lock { margin-top:12px; background:#161616; border:1px solid #232323; border-radius:14px;
  padding:13px 15px; display:flex; align-items:center; gap:11px; font-size:12px; color:#9aa4ac;
  font-weight:600; line-height:1.45; }
.chr-lock span { font-size:18px; }

.chr-share { display:block; width:100%; margin-top:16px; padding:15px; border:none;
  border-radius:26px; background:#18E0A8; color:#053a2c; font-size:14.5px; font-weight:900;
  text-align:center; cursor:pointer; font-family:inherit;
  box-shadow:0 10px 26px rgba(24,224,168,.28); }
.chr-share:disabled { background:#232323; color:#9aa4ac; box-shadow:none; cursor:default; }
.chr-share-secondary { margin-top:10px; background:#1c1c1c; border:1px solid #2a2a2a;
  color:#cfe0ec; box-shadow:none; }
.chr-share-secondary:disabled { background:#1c1c1c; border-color:#232323; color:#7f8b93; }
.chr-spin { display:inline-block; width:14px; height:14px; margin-right:9px; vertical-align:-2px;
  border-radius:50%; border:2px solid rgba(255,255,255,.18); border-top-color:#9aa4ac;
  animation:chrSpin .8s linear infinite; }
.chr-leaderboard-link { display:block; margin-top:12px; text-align:center; font-size:13px;
  font-weight:800; color:#18E0A8; text-decoration:none; }
.chr-leaderboard-link:hover { color:#3ff0bf; }

/* White confirmation toast (matches GmStart's .gm-toast). */
.chr-toast { position:fixed; left:50%; bottom:120px; z-index:2200; transform:translateX(-50%);
  background:#fff; color:#000; font-size:12.5px; font-weight:800; padding:11px 18px;
  border-radius:24px; box-shadow:0 10px 30px rgba(0,0,0,.5); white-space:nowrap;
  animation:chrToastIn .3s ease both; }
@keyframes chrToastIn { from { opacity:0; transform:translate(-50%,14px); }
  to { opacity:1; transform:translate(-50%,0); } }
@keyframes chrSpin { to { transform:rotate(360deg); } }
`;

export default ChallengeResult;
